import React, { useEffect, useId, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { safeArray } from '../../utils/safe';
import type { Flavor } from '../../types/@types/flavor';
import type { Product } from '../../types/@types/product';
import type { LinePriceCalc, OrderItemInput } from '../../types/@types/order';
import { getFlavors } from '../../services/FlavorService';
import { priceCheck } from '../../services/OrderService';
import LinePriceBreakdown from '../Price/LinePriceBreakdown';
import { buildKitchenSummary } from '../../utils/kitchen';
import { Button } from '../ui';

interface SelectedFlavor {
  id: string;
  name: string;
  qty: number;
}

interface Props {
  open: boolean;
  product: Product | null;
  quantity?: number;
  onClose: () => void;
  onConfirm: (payload: {
    flavors?: SelectedFlavor[];
    additions: Record<string, number>;
    exceptions: string[];
    linePriceCalc?: LinePriceCalc;
    kitchenSummary?: string;
  }) => void;
}

export default function FlavorSelectorModal({
  open,
  product,
  quantity = 1,
  onClose,
  onConfirm,
}: Props) {
  const titleId = useId();
  const { data: flavors } = useQuery({
    queryKey: ['flavors'],
    queryFn: getFlavors,
    enabled: open,
  });

  const [selected, setSelected] = useState<Record<string, number>>({});
  const [additions, setAdditions] = useState<Record<string, number>>({});
  const [exceptions, setExceptions] = useState<string[]>([]);
  const [calc, setCalc] = useState<LinePriceCalc | undefined>();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const flavorsCfg = product?.flavors?.enabled
    ? product.flavors
    : product?.maxFlavors
      ? { enabled: product.maxFlavors > 0, min: 0, max: product.maxFlavors }
      : null;
  const additionsCfg = product?.additions?.enabled
    ? product.additions?.options ?? []
    : [];
  const exceptionsCfg = product?.exceptions?.enabled
    ? product.exceptions?.options ?? []
    : [];

  useEffect(() => {
    if (!open) {
      setSelected({});
      setAdditions({});
      setExceptions([]);
      setCalc(undefined);
      setErrorMsg(null);
    }
  }, [open]);

  if (!open || !product) return null;

  const totalSelected = Object.values(selected).reduce((s, v) => s + v, 0);
  const incrementFlavor = (id: string) => {
    if (flavorsCfg) {
      const max = flavorsCfg.max ?? Infinity;
      if (totalSelected >= max) return;
    }
    setSelected((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  };
  const decrementFlavor = (id: string) => {
    setSelected((prev) => {
      const value = (prev[id] || 0) - 1;
      if (value <= 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: value };
    });
  };

  const onIncAddition = (key: string, maxQty: number) =>
    setAdditions((prev) => {
      const next = { ...prev, [key]: Math.min(maxQty, (prev[key] ?? 0) + 1) };
      if (next[key] === 0) delete next[key];
      return next;
    });
  const onDecAddition = (key: string) =>
    setAdditions((prev) => {
      const nextQty = Math.max(0, (prev[key] ?? 0) - 1);
      const next = { ...prev, [key]: nextQty };
      if (nextQty === 0) delete next[key];
      return next;
    });

  const onToggleException = (key: string) =>
    setExceptions((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  useEffect(() => {
    if (!flavorsCfg) {
      setErrorMsg(null);
      return;
    }
    const min = flavorsCfg.min ?? 0;
    const max = flavorsCfg.max ?? Infinity;
    if (totalSelected < min)
      setErrorMsg(`Selecciona al menos ${min} sabor(es)`);
    else if (totalSelected > max)
      setErrorMsg(`Máximo ${max} sabor(es)`);
    else setErrorMsg(null);
  }, [totalSelected, flavorsCfg]);

  const priceCheckMutation = useMutation({
    mutationKey: ['orders', 'price-check'],
    mutationFn: (items: OrderItemInput[]) => priceCheck({ items }),
  });

  useEffect(() => {
    if (!product?.id) return;
    const t = setTimeout(async () => {
      try {
        const items: OrderItemInput[] = [
          {
            product: String(product.id),
            quantity,
            flavors: Object.entries(selected).map(([flavorId, qty]) => ({
              flavorId,
              qty,
            })),
            additions,
            exceptions,
          },
        ];
        const res = await priceCheckMutation.mutateAsync(items);
        const first = res?.items?.[0];
        setCalc(first?.linePriceCalc);
      } catch {
        /* silent */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [product?.id, quantity, selected, additions, exceptions, priceCheckMutation]);

  const handleConfirm = () => {
    if (errorMsg) return;
    const selectedFlavors: SelectedFlavor[] = Object.entries(selected).map(
      ([id, qty]) => {
        const flavor = flavors?.find((f) => f.id === id);
        return { id, name: flavor?.name || '', qty };
      },
    );
    const flavorsNames = selectedFlavors.map((f) => f.name);
    const kitchenSummary = buildKitchenSummary({
      flavorsNames,
      additions,
      additionsCatalog: additionsCfg,
      exceptions,
      exceptionsCatalog: exceptionsCfg,
    });
    onConfirm({
      flavors: selectedFlavors,
      additions,
      exceptions,
      linePriceCalc: calc,
      kitchenSummary,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-6">
      <div
        className="absolute inset-0 bg-black/35 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-[1] flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-divider bg-surface shadow-[0_12px_32px_rgba(15,23,42,0.28)]"
      >
        <header className="flex flex-col gap-1 border-b border-border px-5 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-text">Seleccionar Sabores</h2>
          {flavorsCfg && (
            <p className="text-sm text-text-muted">
              Seleccionados {totalSelected}/{flavorsCfg.max ?? '∞'}
            </p>
          )}
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {flavorsCfg && (
            <ul className="space-y-2">
              {safeArray(flavors).map((flavor) => {
                if (
                  flavorsCfg.allowed &&
                  flavorsCfg.allowed.length > 0 &&
                  !flavorsCfg.allowed.includes(flavor.id)
                )
                  return null;
                const qty = selected[flavor.id] || 0;
                return (
                  <li
                    key={flavor.id}
                    className="flex items-center justify-between rounded-xl border border-border bg-surface px-3 py-2.5"
                  >
                    <span className="text-sm font-medium text-text">
                      {flavor.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-lg font-semibold text-text transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-40"
                        onClick={() => decrementFlavor(flavor.id)}
                        disabled={qty === 0}
                        aria-label={`Quitar ${flavor.name}`}
                      >
                        -
                      </button>
                      <span className="min-w-[2ch] text-center text-base font-semibold tabular-nums text-text">
                        {qty}
                      </span>
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-lg font-semibold text-text transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-40"
                        onClick={() => incrementFlavor(flavor.id)}
                        disabled={
                          flavorsCfg &&
                          totalSelected >= (flavorsCfg.max ?? Infinity)
                        }
                        aria-label={`Agregar ${flavor.name}`}
                      >
                        +
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {additionsCfg.length > 0 && (
            <section className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-text">Adiciones</h4>
              <div className="space-y-2">
                {additionsCfg
                  .filter((o) => o.active)
                  .map((opt) => {
                    const qty = additions[opt.key] ?? 0;
                    return (
                      <div
                        key={opt.key}
                        className="flex items-center justify-between rounded-xl border border-border bg-surface px-3 py-2.5"
                      >
                        <span className="text-sm font-medium text-text">
                          {opt.name}{' '}
                          <span className="text-xs text-text-muted">(+${opt.priceDelta})</span>
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-lg font-semibold text-text transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-40"
                            onClick={() => onDecAddition(opt.key)}
                            disabled={qty <= 0}
                            aria-label={`Quitar ${opt.name}`}
                          >
                            -
                          </button>
                          <span className="min-w-[2ch] text-center text-base font-semibold tabular-nums text-text">
                            {qty}
                          </span>
                          <button
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-lg font-semibold text-text transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-40"
                            onClick={() => onIncAddition(opt.key, opt.maxQty)}
                            disabled={qty >= opt.maxQty}
                            aria-label={`Agregar ${opt.name}`}
                          >
                            +
                          </button>
                          <span className="text-xs text-text-muted">
                            / {opt.maxQty}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </section>
          )}

          {exceptionsCfg.length > 0 && (
            <section className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-text">Sin / Excepciones</h4>
              <div className="flex flex-wrap gap-2">
                {exceptionsCfg
                  .filter((o) => o.active)
                  .map((opt) => {
                    const on = exceptions.includes(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => onToggleException(opt.key)}
                        className={`inline-flex min-h-[2.5rem] items-center justify-center rounded-full border px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 ${on
                            ? 'border-primary bg-primary text-white'
                            : 'border-border bg-surface text-text hover:bg-muted'
                          }`}
                        aria-pressed={on}
                      >
                        {opt.name}
                      </button>
                    );
                  })}
              </div>
            </section>
          )}

          <div className="mt-4">
            <LinePriceBreakdown calc={calc} />
          </div>
        </div>
        <footer className="sticky bottom-0 border-t border-border bg-white px-5 py-3 shadow-receipt">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs font-semibold text-red-500" role="status">
              {errorMsg ?? ''}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="md" onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="primary" size="md" disabled={!!errorMsg} onClick={handleConfirm}>
                Confirmar
              </Button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
